import { makeProcessOriginExporter } from './processOriginExporter';
import {
  AutocompleteInput,
  Datagrid,
  DateField,
  Create,
  Edit,
  EditButton,
  ReferenceInput,
  Resource,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  required,
} from "react-admin";
import EllipsisTextField from "../components/EllipsisTextField";
import VersionedList, { DbVersionFilterInput } from "../components/DbVersion";

const TYPE_OPTIONS = [
  { id: "Generic", name: "Generic" },
  { id: "Custom", name: "Custom" },
  { id: "Specific", name: "Specific" },
  { id: "Multi-process", name: "Multi-process" },
];

const trimmingOriginExporter = makeProcessOriginExporter('trimming');

const TrimmingOriginFilters = [
  <ReferenceInput
    key="trimmingOriginTrimmingSearch"
    label="Search by trimming"
    source="trimmingId"
    reference="trimming"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <ReferenceInput
    key="trimmingOriginCountrySearch"
    label="Search by country"
    source="originId"
    reference="origins"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <DbVersionFilterInput key="trimmingOriginDbVersion" alwaysOn />,
];

const TrimmingOriginList = (props) => (
  <VersionedList
    {...props}
    resource="processOrigin/trimming"
    filters={TrimmingOriginFilters}
    exporter={trimmingOriginExporter}
  >
    <div style={{ marginTop: "15px" }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <TextField label="Trimming" source="trimming.name" />
        <TextField label="Country" source="origin.name" />
        <TextField label="Database context" source="databaseContext.name" emptyText="" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="water" label="Water scarcity (m3 water eq)" />
        <TextField source="waterConsumption" label="Water consumption (liters water)" />
        <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
        <TextField source="eutrophication" label="Eutrophication (g P eq)" />
        <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
        <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
        <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
        <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
        <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
        <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
        <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
        <TextField source="acidification" label="Acidification (mol H+ eq)" />
        <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
        <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
        <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
        <TextField source="landUse" label="Land Use (pt)" />
        <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
        <EllipsisTextField source="description" label="Description" />
        <EllipsisTextField source="primarySource" label="Primary source" />
        <EllipsisTextField source="dataGenerator" label="Data generator" />
        <EllipsisTextField source="impactGenerator" label="Impact generator" />
        <EllipsisTextField source="originalDataset" label="Original dataset" />
        <EllipsisTextField source="systemBoundaries" label="System boundaries" />
        <EllipsisTextField source="allocation" label="Allocation" />
        <EllipsisTextField source="mainAssumptions" label="Main assumptions" />
        <TextField source="dataValidity" label="Data validity" />
        <TextField source="dataQuality" label="Data quality" />
        <TextField source="referenceYear" label="Reference year" />
        <TextField source="teR" label="teR" />
        <TextField source="geR" label="geR" />
        <TextField source="tiR" label="tiR" />
        <TextField source="precision" label="Precision" />
        <TextField source="type" label="Type" />
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowTrimmingOrigin = (props) => (
  <Show {...props} resource="processOrigin/trimming">
    <SimpleShowLayout>
      <TextField source="id" />
      <TextField label="Trimming" source="trimming.name" />
      <TextField label="Country" source="origin.name" />
      <TextField label="Database context" source="databaseContext.name" />
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
      <TextField source="water" label="Water scarcity (m3 water eq)" />
      <TextField source="waterConsumption" label="Water consumption (liters water)" />
      <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
      <TextField source="eutrophication" label="Eutrophication (g P eq)" />
      <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
      <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextField source="acidification" label="Acidification (mol H+ eq)" />
      <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextField source="landUse" label="Land Use (pt)" />
      <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
      <TextField source="description" label="Description" />
      <TextField source="primarySource" label="Primary source" />
      <TextField source="dataGenerator" label="Data generator" />
      <TextField source="impactGenerator" label="Impact generator" />
      <TextField source="originalDataset" label="Original dataset" />
      <TextField source="systemBoundaries" label="System boundaries" />
      <TextField source="allocation" label="Allocation" />
      <TextField source="mainAssumptions" label="Main assumptions" />
      <TextField source="dataValidity" label="Data validity" />
      <TextField source="dataQuality" label="Data quality" />
      <TextField source="referenceYear" label="Reference year" />
      <TextField source="teR" label="teR" />
      <TextField source="geR" label="geR" />
      <TextField source="tiR" label="tiR" />
      <TextField source="precision" label="Precision" />
      <TextField source="type" label="Type" />
    </SimpleShowLayout>
  </Show>
);

const CreateTrimmingOrigin = (props) => (
  <Create {...props} resource="processOrigin/trimming">
    <SimpleForm>
      <ReferenceInput
        label="Trimming"
        source="trimmingId"
        reference="trimming"
        filterToQuery={searchText => ({ name: searchText })}
        validate={[required()]}
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput
        label="Country"
        source="originId"
        reference="origins"
        filterToQuery={searchText => ({ name: searchText })}
        validate={[required()]}
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput
        label="Database context"
        source="databaseContextId"
        reference="database-context"
        filterToQuery={searchText => ({ name: searchText })}
        validate={[required()]}
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={TYPE_OPTIONS} />
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Create>
);

const EditTrimmingOrigin = (props) => (
  <Edit {...props} resource="processOrigin/trimming">
    <SimpleForm>
      <ReferenceInput
        label="Trimming"
        source="trimmingId"
        reference="trimming"
        filterToQuery={searchText => ({ name: searchText })}
        validate={[required()]}
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput
        label="Country"
        source="originId"
        reference="origins"
        filterToQuery={searchText => ({ name: searchText })}
        validate={[required()]}
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput
        label="Database context"
        source="databaseContextId"
        reference="database-context"
        filterToQuery={searchText => ({ name: searchText })}
        validate={[required()]}
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={TYPE_OPTIONS} />
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Edit>
);

const trimmingOriginResource = () => (
  <Resource
    name="processOrigin/trimming"
    options={{ label: "Trimming", menuGroup: "Bcome" }}
    list={TrimmingOriginList}
    create={CreateTrimmingOrigin}
    edit={EditTrimmingOrigin}
    show={ShowTrimmingOrigin}
    path="processOrigin/trimming"
  />
);

export { trimmingOriginResource };
